import { addDays, format, parse } from 'date-fns';
import { useEffect, useState } from 'react';
import { fetchAppointment } from '../providers/appointments';
import { fetchProfessionals } from '../providers/professionals';
import { fetchSpecialties } from '../providers/specialties';
import { fetchAdminPatients } from '../providers/patients';
import { Field, Input, Label, Select } from './Field';
import RadioGroup from './RadioGroup';
import { fetchSlots } from '../providers/slots';
import { fetchClinics } from '../providers/clinics';
import { useTranslations } from '../providers/i18n';

function AdminAppointmentForm({ appointmentId }) {
    const [t, translationsLoaded] = useTranslations('appointment-form');
    const [ready, setReady] = useState(false);

    const [clinicId, setClinicId] = useState('');
    const [specialtyId, setSpecialtyId] = useState('');
    const [professionalId, setProfessionalId] = useState('');
    const [patientId, setPatientId] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [video, setVideo] = useState(0);

    const [clinics, setClinics] = useState([]);
    const [patients, setPatients] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const [professionals, setProfessionals] = useState([]);
    const [slots, setSlots] = useState([]);

    const [existingAppointmentDate, setExistingAppointmentDate] = useState('');

    const isVideoAvailable = () => !!specialties.find(({ id }) => id == specialtyId)?.video;

    useEffect(() => {
        const promises = [];

        promises.push(fetchClinics().then(setClinics));

        if (appointmentId) {
            promises.push(
                fetchAppointment(appointmentId).then((appointment) => {
                    setClinicId(appointment.clinic.id);
                    setSpecialtyId(appointment.specialty.id);
                    setProfessionalId(appointment.professional.id);
                    setPatientId(appointment.patient.id);
                    setVideo(appointment.video);
                    setDate(format(new Date(appointment.date), 'yyyy-MM-dd'));
                    setTime(format(new Date(appointment.date), 'HH:mm'));

                    setExistingAppointmentDate(new Date(appointment.date));
                })
            );
        } else {
            setDate(format(new Date(), 'yyyy-MM-dd'));
        }

        Promise.all(promises).then(() => {
            setReady(true);
        });
    }, []);

    useEffect(() => {
        if (clinicId) {
            fetchAdminPatients(clinicId).then(setPatients);
            fetchSpecialties({
                clinic_id: clinicId,
            }).then(setSpecialties);
        }
    }, [clinicId]);

    useEffect(() => {
        if (specialtyId) {
            fetchProfessionals({
                clinic_id: clinicId,
                specialty_id: specialtyId,
            }).then(setProfessionals);
        } else {
            setProfessionals([]);
        }
    }, [clinicId, specialtyId]);

    useEffect(() => {
        if (specialtyId && !specialties.some(({ id }) => id == specialtyId)) {
            setSpecialtyId('');
        }
    }, [specialties]);

    useEffect(() => {
        if (professionalId && !professionals.some(({ id }) => id == professionalId)) {
            setProfessionalId('');
        }
    }, [professionals]);

    useEffect(() => {
        if (!isVideoAvailable()) {
            setVideo(0);
        }
    }, [specialtyId]);

    useEffect(() => {
        if (professionalId && date) {
            const fromDate = parse(`${date} 00:00`, 'yyyy-MM-dd HH:mm', new Date());
            const from = fromDate.toISOString();
            const to = addDays(fromDate, 1).toISOString();
            fetchSlots({ professional_id: professionalId, from, to }).then(setSlots);
        } else {
            setSlots([]);
        }
    }, [professionalId, date]);

    useEffect(() => {
        if (time && existingAppointmentDate && !slots.some(({ start }) => new Date(start).getTime() == existingAppointmentDate.getTime())) {
            setTime('');
        }
    }, [slots]);

    if (!ready) return null;
    if (!translationsLoaded) return null;

    return (
        <div className="space-y-4">
            <Field>
                <Label>
                    <span>{t('clinic')}</span>
                    <Select
                        value={clinicId}
                        options={clinics}
                        disabled={!clinics.length || appointmentId}
                        onChange={(e) => {
                            setClinicId(e.target.value);
                        }}
                    />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('patient')}</span>
                    <Select
                        name="patient_id"
                        value={patientId}
                        options={patients}
                        disabled={!patients.length || appointmentId}
                        onChange={(e) => {
                            setPatientId(e.target.value);
                        }}
                    />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('specialty')}</span>
                    <Select
                        name="specialty_id"
                        value={specialtyId}
                        options={specialties}
                        disabled={!patientId || appointmentId}
                        onChange={(e) => {
                            setSpecialtyId(e.target.value);
                        }}
                    />
                </Label>
            </Field>

            {specialtyId && (
                <Field>
                    <Label>{t('format')}</Label>
                    <input type="hidden" name="video" value={video} readOnly />
                    <RadioGroup
                        value={video}
                        onChange={setVideo}
                        options={[
                            {
                                value: 0,
                                label: t('in-person'),
                            },
                            {
                                value: 1,
                                label: t('video-appointment'),
                                disabled: !isVideoAvailable(),
                            },
                        ]}
                    />
                </Field>
            )}

            <Field>
                <Label>
                    <span>{t('professional')}</span>
                    <Select
                        name="professional_id"
                        value={professionalId}
                        options={professionals.map(({ id, titled_name: name }) => ({ id, name }))}
                        disabled={!professionals.length}
                        onChange={(e) => {
                            setProfessionalId(e.target.value);
                        }}
                    />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('date')}</span>
                    <Input
                        type="date"
                        value={date}
                        disabled={!professionalId}
                        onChange={(e) => {
                            setDate(e.target.value);
                        }}
                    />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('time')}</span>
                </Label>
                <input type="hidden" name="date" value={date && time ? parse(`${date} ${time}`, 'yyyy-MM-dd HH:mm', new Date()).toISOString() : ''} readOnly />
                <div className="flex gap-2">
                    {slots.length ? (
                        slots.map(({ start, isAvailable }) => {
                            const slotTime = format(new Date(start), 'HH:mm');
                            const active = slotTime == time;
                            const isCurrent = existingAppointmentDate ? new Date(start).getTime() == existingAppointmentDate.getTime() : false;
                            return (
                                <button
                                    key={slotTime}
                                    type="button"
                                    disabled={!isAvailable && !isCurrent}
                                    className={`${
                                        active ? 'cursor-default border-blue-400 bg-blue-100 text-blue-700' : 'border-blue-100 bg-blue-50 text-blue-600'
                                    } rounded border px-3 py-2 disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400`}
                                    onClick={() => {
                                        setTime(slotTime);
                                    }}
                                >
                                    {slotTime}
                                </button>
                            );
                        })
                    ) : (
                        <span className="px-3 py-2 text-gray-400 bg-gray-100 border border-gray-200 rounded">{t('no-slots')}</span>
                    )}
                </div>
            </Field>
        </div>
    );
}

export default AdminAppointmentForm;
